import { useState } from 'react';
import { type MessageKey, useT } from '../../i18n/index.js';
import type { PauseOnDanger } from '../../pause/detectDangers.js';
import type { MatchConfigOverrides } from '../types/MatchConfigOverrides.js';
import type { CinematicsSetting, CombatOverlayMode } from '../useSettings.js';
import { useSettings } from '../useSettings.js';
import { MatchSettingsTab } from './MatchSettingsTab.js';
import { SegmentedRow, ToggleRow } from './shared.js';

const COMBAT_OVERLAY_OPTIONS: ReadonlyArray<{
  readonly value: CombatOverlayMode;
  readonly label: MessageKey;
}> = [
  { value: 'off', label: 'common.off' },
  { value: 'drawer', label: 'settings.gameplay.combatOverlay.drawer' },
  { value: 'modal+drawer', label: 'settings.gameplay.combatOverlay.modal' },
];

const PAUSE_ON_DANGER_OPTIONS: ReadonlyArray<{
  readonly value: PauseOnDanger;
  readonly label: MessageKey;
}> = [
  { value: 'off', label: 'common.off' },
  { value: 'major', label: 'settings.gameplay.pauseDanger.major' },
  { value: 'all', label: 'settings.gameplay.pauseDanger.all' },
];

const CINEMATICS_OPTIONS: ReadonlyArray<CinematicsSetting> = ['all', 'big', 'off'];

export function GameplayTab() {
  const t = useT();
  const cinematics = useSettings((s) => s.cinematics);
  const setCinematics = useSettings((s) => s.setCinematics);
  const localize = <T extends string>(
    options: ReadonlyArray<{ readonly value: T; readonly label: MessageKey }>,
  ) => options.map((o) => ({ value: o.value, label: t(o.label) }));
  const autosaveEnabled = useSettings((s) => s.autosaveEnabled);
  const showTileTooltips = useSettings((s) => s.showTileTooltips);
  const combatOverlayMode = useSettings((s) => s.combatOverlayMode);
  const setAutosaveEnabled = useSettings((s) => s.setAutosaveEnabled);
  const setShowTileTooltips = useSettings((s) => s.setShowTileTooltips);
  const setCombatOverlayMode = useSettings((s) => s.setCombatOverlayMode);
  const autoPauseWhenAway = useSettings((s) => s.autoPauseWhenAway);
  const setAutoPauseWhenAway = useSettings((s) => s.setAutoPauseWhenAway);
  const pauseOnDanger = useSettings((s) => s.pauseOnDanger);
  const setPauseOnDanger = useSettings((s) => s.setPauseOnDanger);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchConfig, setMatchConfig] = useState<MatchConfigOverrides>({});

  return (
    <div>
      <h3 className="text-sm font-medium text-foreground mb-4">{t('settings.gameplay.title')}</h3>
      <ToggleRow
        label={t('settings.gameplay.autosave')}
        description={t('settings.gameplay.autosaveHint')}
        value={autosaveEnabled}
        onChange={setAutosaveEnabled}
      />
      <ToggleRow
        label={t('settings.gameplay.tooltips')}
        description={t('settings.gameplay.tooltipsHint')}
        value={showTileTooltips}
        onChange={setShowTileTooltips}
      />
      <ToggleRow
        label={t('settings.gameplay.pauseAway')}
        description={t('settings.gameplay.pauseAwayHint')}
        value={autoPauseWhenAway}
        onChange={setAutoPauseWhenAway}
      />
      <SegmentedRow
        label={t('settings.gameplay.pauseDanger')}
        description={t('settings.gameplay.pauseDangerHint')}
        value={pauseOnDanger}
        options={localize(PAUSE_ON_DANGER_OPTIONS)}
        onChange={setPauseOnDanger}
      />
      <SegmentedRow
        label={t('settings.cinematics')}
        description={t('settings.cinematics.description')}
        value={cinematics}
        options={CINEMATICS_OPTIONS.map((value) => ({
          value,
          label: t(`settings.cinematics.${value}`),
        }))}
        onChange={setCinematics}
      />
      <SegmentedRow
        label={t('settings.gameplay.combatOverlay')}
        description={t('settings.gameplay.combatOverlayHint')}
        value={combatOverlayMode}
        options={localize(COMBAT_OVERLAY_OPTIONS)}
        onChange={setCombatOverlayMode}
      />
      <div className="mt-4 border-t border-border pt-4">
        <button
          type="button"
          onClick={() => setMatchOpen(!matchOpen)}
          className="flex w-full cursor-pointer items-center justify-between text-left"
        >
          <span className="text-xs text-foreground font-medium">
            {t('settings.gameplay.matchSettings')}
          </span>
          <span
            className={`text-muted-foreground text-xs transition-transform ${matchOpen ? 'rotate-180' : ''}`}
          >
            &#9660;
          </span>
        </button>
        {matchOpen && (
          <div className="mt-3 pl-2">
            <MatchSettingsTab config={matchConfig} onChange={setMatchConfig} />
          </div>
        )}
      </div>
    </div>
  );
}
